module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h3', {
    staticClass: "h3b",
    attrs: {
      "id": "ch01"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_3"
    }
  }), _c('strong', [_c('em', [_vm._v("Chapter 1")]), _c('br'), _vm._v("A Global Perspective on Sephardic and Mizrahi Jewry"), _c('br'), _c('em', [_vm._v("An Introductory Essay")])])]), _vm._v(" "), _c('p', {
    staticClass: "chapauthor"
  }, [_c('em', [_vm._v("Zion Zohar")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_c('em', [_vm._v("Sephardic and Mizrahi Jewry: From the Golden Age of Spain to Modern Times")]), _vm._v(" seeks to introduce readers to some of the most important aspects of the history, culture, and thought of Sephardic and Mizrahi Jews. The significance of Sephardic/Mizrahi studies lies not only internally—for Jews in Israel and the Diaspora—but is also of great value to the world at large, given the contributions of Sephardic and Mizrahi Jewry throughout history.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The idea for this book was born from a realization by the editor that no published work exists that simultaneously meets all of the following goals: a) including the latest findings by scholars regarding key topics in the study of Sephardic and Mizrahi Jewry in English; b) being written in language accessible to undergraduate students and lay people, yet presenting material at a superior academic level in the field; and c) exposing the reader to the thinking of many of the most prominent scholars in the field regarding their area of expertise. I hope that this book will become a companion work to the many excellent textbooks of Sephardic Jewish history used by anyone teaching in the field of Sephardic studies or seeking knowledge of this fascinating area of the history of Jewish civilization. Each chapter of this book seeks to summarize the author’s and other scholars’ findings so as to present a wide spectrum of topics in the most comprehensive way.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The history of Sephardic and Mizrahi civilization may be divided into three major parts. The first part covers the period from the origin of Sephardic culture to the expulsion from Spain in 1492. The second part "), _c('a', {
    attrs: {
      "id": "page_4"
    }
  }), _vm._v("covers the period between 1492 and the beginning of the modern age, a time in which the Sephardic Diaspora became a distinct phenomenon in Jewish history. The third period covers the modern era and contemporary accounts of Sephardic existence in the State of Israel and abroad. This book is structured according to these three historical and chronological stages.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("At the outset, we must of course address two key questions: What constitutes Sephardic and Mizrahi Jewry? And what differentiates these groups from Ashkenazi Jews?")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("Oriental Jews, Ashkenazim, and Sephardim")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("The question of precisely when each of these three groups originated is not entirely clear and is one that I leave to social historians to settle. However, generally speaking, Mizrahi Jewry can trace its origins back the farthest—to the forced exile from the Land of Israel to Babylonia (modern Iraq) in the year 586 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BCE")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en1"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en1"
    }
  }, [_vm._v("1")])]), _vm._v(" Thus, Iraqi Jewry can claim to be among the oldest Diaspora communities in the Jewish world. With the mass exodus of the majority of Jews from Iraq in the modern era (1950s) and continued emigration thereafter, that community no longer exists apart from a few individuals numbering fewer than a hundred souls.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Around that same time in the sixth century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BCE")]), _vm._v(", Jews also fled to Egypt where they established themselves as well."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en2"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en2"
    }
  }, [_vm._v("2")])]), _vm._v(" Following the assassination of the military governor Gedaliah, the Jewish leadership that remained behind after the initial exile fled to Egypt with the prophet Jeremiah, who reports these events in detail. In the ensuing centuries, Egyptian Jewry too would grow, founding communities in such diverse locales as Elephantine, near the modern city of Aswan, and in the multi-ethnic port city of Alexandria. Additionally, during the five centuries preceding the Common Era, other Oriental Jewish communities grew in stature in the Diaspora, such as Greece,"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en3"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en3"
    }
  }, [_vm._v("3")])]), _vm._v(" Syria, Asia Minor (modern Turkey), Cyprus and Crete, among other islands in the Mediterranean, as well as Cyrenaica (modern Libya)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en4"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en4"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("During the period stretching from the destruction of the Second Temple in 70 "), _c('span', {
    staticClass: "small"
  }, [_vm._v("CE")]), _vm._v(" until the rise of Islam (early seventh century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("CE")]), _vm._v("), a great many developments took place in Oriental Jewish history. For example, at the start of the period, the majority of Jews lived within the Roman Empire, though a significant minority still resided in Babylonia and its environs "), _c('a', {
    attrs: {
      "id": "page_5"
    }
  }), _vm._v("under the Parthian regime and its successors. By the end of this era, most of the Jewish population worldwide and all of its important centers had come under Muslim rule."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en5"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en5"
    }
  }, [_vm._v("5")])]), _vm._v(" Culturally, Jews who lived under Rome were naturally shaped by the prevailing Hellenistic-Roman civilization of their surroundings, whereas those dwelling in the area of Babylonia formed their own distinct patterns of life outside the sphere of Hellenistic and Roman influence."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en6"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en6"
    }
  }, [_vm._v("6")])]), _vm._v(" These developments, as will become apparent below, affected the creation of the Ashkenazim and Sephardim as separate Jewish subgroups later on.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Following the Great Revolt (66–"), _c('span', {
    staticClass: "small"
  }, [_vm._v("70 CE")]), _vm._v(") as well as other subsequent Jewish insurrections, the situation of Jews in the Roman Empire began to decline. Upon Emperor Constantine’s conversion from paganism to Christianity in "), _c('span', {
    staticClass: "small"
  }, [_vm._v("313 CE")]), _vm._v(", the decline intensified, such that the community in the Land of Israel soon lost its place of prominence among world Jewry. As a result, from approximately the fourth through the tenth centuries, a new leading center of Jewish life emerged in Babylonia along the Tigris and Euphrates Rivers. It was here that the Babylonian Talmud—the holiest and most authoritative Jewish text second to the Bible—reached completion by the end of the sixth century, representing the culmination of Babylonian Jewry’s productivity over more than a thousand years.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("From the Muslim conquest onward, the vast majority of Oriental Jews lived under Islam, establishing themselves at one time or another in almost all the known Muslim and Arab countries throughout the world. Thus, while the Sephardi and Ashkenazi subgroups originated and lived to a greater or lesser extent under Christian regimes, Oriental Jewry, though older than Islam itself, reached maturity and developed its own particular character under Islam, with the exception of most of the Jews of India and the Far East."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en7"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en7"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Ashkenazim are the descendants of Jews who first settled in the Rhine River valley (Germany) and northern France during the era of Roman rule and over subsequent centuries."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en8"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en8"
    }
  }, [_vm._v("8")])]), _vm._v(" Many of them later migrated eastward to Poland-Lithuania and other Eastern European areas because of the forced expulsions"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en9"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en9"
    }
  }, [_vm._v("9")])]), _vm._v(" decreed by Western European monarchs and the persecutions accompanying the Crusades (eleventh through thirteenth centuries). In the seventeenth and eighteenth centuries, the Ashkenazim’s inexorable march eastward reversed course as some Ashkenazi Jews returned to Western Europe to escape pogroms and poverty in the East. Despite the twisted path Ashkenazi Jewry took from one country and "), _c('a', {
    attrs: {
      "id": "page_6"
    }
  }), _vm._v("region to another, their movement always occurred within Christendom. Thus, Ashkenazi Jewry developed and matured solely within the context of Christian majority culture.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Mark R. Cohen, in "), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02"
    }
  }, [_vm._v("chapter 2")]), _vm._v(" of this book, following the views of Bernard Lewis,"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en10"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en10"
    }
  }, [_vm._v("10")])]), _vm._v(" will suggest that world Jewry can be divided into two main groupings—those Jews who lived “under the crescent” (under Islam) and those who lived “under the cross” (under Christianity). Oriental Jewry, as we have pointed out, lived almost exclusively under the crescent. Cohen argues that the Sephardim constitute a third entity, bearing similarities to both Jews under the crescent and Jews under the cross, and suggests that their real origins lie under the crescent in the medieval Arab world.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Sephardim are the descendents of Jews who had at one time lived on the Iberian Peninsula (Spain and Portugal). Jews initially settled in Spain during Roman times and endured life there during Christian rule, though legends tell of Jews living there as early as King Solomon’s time. However, as will be explored at length in several chapters, Sephardic culture reached its full flowering following the Muslim conquest in 711 and for the next several centuries, under Islam. The Christian reconquest of Spain, conducted in earnest during the twelfth century, gradually brought most Jews under Christian rule once again until they were forcibly expelled from the peninsula at the close of the fifteenth century, though some chose the path of remaining and conducting Jewish practice in secret."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en11"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en11"
    }
  }, [_vm._v("11")])]), _vm._v(" Those who elected to leave migrated to the Ottoman Empire"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en12"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en12"
    }
  }, [_vm._v("12")])]), _vm._v(" in large numbers as well as to the Maghreb (North Africa), parts of Italy, and the city of Amsterdam in Holland. In time, many secret Jews, known as “Marranos” or crypto-Jews, also journeyed overseas to the Spanish and Portuguese colonies in North and South America.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("Distinctions between Sephardim and Ashkenazim")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("One of the main distinctions between Sephardim and Ashkenazim"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en13"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en13"
    }
  }, [_vm._v("13")])]), _vm._v(" historically has been the Sephardic exposure to a relatively more tolerant and often welcoming culture under Muslim rule. During the Golden Age of Spain (in the tenth through the twelfth centuries), Muslim rulers encouraged a sophisticated cultural legacy that was distinctive from Islam, thus allowing Jews and other minorities to partake in this cultural legacy without feeling any pressure to convert. Consequently, Sephardim are historically distinguished by several features: a) their desire for and attainment of "), _c('a', {
    attrs: {
      "id": "page_7"
    }
  }), _vm._v("secular political positions; b) their ability to appreciate and harmonize religion and secular aspects of culture; c) their skill at mastering both religious works (like the study of the Bible and Talmud) and more secular subjects (such as poetry and philosophy); and d) their multicultural proficiency, which enabled them to converse and publish in both Hebrew and Arabic. Because of their acceptance into Muslim society and culture, Sephardim were more open to external influences and more tolerant of differences.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("By contrast, Ashkenazi Jewry originated first in a Hellenistic-Roman culture and subsequently under Catholic hegemony, both of which were far less tolerant than either the Babylonian culture that nurtured Oriental Jewry or the Islamic one that gave birth to the Sephardic Golden Age. After centuries of living under oppressive conditions in Catholic countries where the high culture was defined and dictated primarily by the Church, Ashkenazi Jews intentionally closed themselves to any outside cultural and intellectual influences. Instead, they immersed themselves almost solely in internal Jewish sources, ideas, and customs, fearing that a deeper exposure to Christian culture might shake the foundations of their faith. As a result, the average Ashkenazi rabbi’s sphere of interest was generally circumscribed by study of the Bible and Talmud to the nearly total exclusion of other sources of wisdom. In addition, Ashkenazi rabbis were far stricter in matters of “halakha.” For example, Rabbi Asher ben Jehiel, born and educated in Germany, wrote, after settling in Toledo, Spain: “Although I know nothing of their secular wisdom,” referring to those who held rationalistic views among the Sephardic political and rabbinic leadership, “blessed be the Merciful God who spared me from it. For examples and evidences come along for the purpose of diverting man from the fear of God and His Torah.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en14"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en14"
    }
  }, [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("There were times when Sephardim also turned inward, debating whether the exposure to and assimilation of other cultures was indeed a positive development, especially when exposed to Christian persecution before the expulsion from Spain in 1492. However, once the trauma of Christian persecution had worn off, many Sephardim settled in lands where they again enjoyed a fair measure of security and were relatively free to practice Judaism. This may be the reason why, even after the trauma of the expulsion from Spain, many Sephardim still displayed a more sympathetic attitude toward outside culture than Ashkenazim and were, on the whole, more inclined to seek knowledge beyond the “four cubits of the law.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_8"
    }
  }), _vm._v("While Sephardim do not differ from Ashkenazim regarding the basic tenets of Judaism (for example, both groups view the Talmud as their ultimate authority in belief and practice), there are many differences in matters of custom and outlook. Sephardim follow the rulings of Rabbi Joseph Caro, a Spanish Jew, in his work, the "), _c('em', [_vm._v("Shulhan Arukh")]), _vm._v(", the accepted code of Jewish law, whereas Ashkenazim adhere to the particular traditions outlined in the same work by Rabbi Moses Isserles, a Polish Jew. In general, Rabbi Caro’s perspective represents a more liberal and permissive tendency than that approved by Ashkenazi authorities such as Isserles. Developing as they did under such different conditions, the Ashkenazi and Sephardi communities gradually established separate customs, norms, and characteristics, which led to differences in ritual, pronunciation of Hebrew, and the liturgical rite, among many other factors.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("A Brief History of the Sephardim")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("The history of Jews living on the Iberian Peninsula stretches back, according to some legends, as early as the exile from Judea in "), _c('span', {
    staticClass: "small"
  }, [_vm._v("586 BCE")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en15"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en15"
    }
  }, [_vm._v("15")])]), _vm._v(" At any rate, Jewish presence in Spain is very old, dating at least as far back as Roman times."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en16"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en16"
    }
  }, [_vm._v("16")])]), _vm._v(" The great historian of Spanish Jewry Eliyahu Ashtor described the life of the Jews during Roman times and subsequently under Visigoth rulers in the following manner:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("The Jewish settlement on the Iberian Peninsula was a very ancient one and in its early stages had prospered. Even after the Visigoths had established their rule over the land, the condition of the Jewish communities remained favorable for a long time. They earned their livelihood with dignity, and they fulfilled the laws of the Torah and observed its commandments without hindrance."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en17"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en17"
    }
  }, [_vm._v("17")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Shortly after the Visigothic regime adopted Roman Catholicism in the late sixth century, Catholic clergy assembled at synods where they passed anti-Jewish legislation that made the life of Spanish Jewry intolerable."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en18"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en18"
    }
  }, [_vm._v("18")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("After a time, the government legitimized forced baptisms, creating the first cases of “anusim,” namely, Jews who were forced to profess Catholicism publicly while practicing Judaism in secret. Thus, when Muslims crossed the Straits of Gibraltar from North Africa in "), _c('span', {
    staticClass: "small"
  }, [_vm._v("711 CE")]), _vm._v(" and invaded the Iberian Peninsula, Jews welcomed them as liberators from Christian "), _c('a', {
    attrs: {
      "id": "page_9"
    }
  }), _vm._v("persecution."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en19"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en19"
    }
  }, [_vm._v("19")])]), _vm._v(" The relatively small band of Muslim conquerors, in turn, entrusted Jews to watch over the cities as they continued their march through Spain. They were also naturally more wary of Christians, against whom they were fighting. Later, they awarded Jews positions of prominence in civic life and in some rare cases, high positions in the military as well. The relatively tolerant Muslim rulers welcomed and esteemed Sephardic Jews who were adept political advisers, skilled financial managers, gifted writers, learned scholars, and pioneering scientists.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The Muslim invasion and conquest of the Iberian Peninsula also enabled the creation of a closer political and linguistic link between the Jews of Spain, situated at the heart of a newly formed Muslim land, and the Jews of Babylonia, the dwelling place of many of the foremost Jewish spiritual authorities at that time. This association gave the Babylonian community the opportunity to pass on their traditions to Iberian Jewry and assisted the Sephardim in assuming leadership of the majority of the world Jewish community shortly after the turn of the millennium."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en20"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en20"
    }
  }, [_vm._v("20")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Born during this era of Islamic rule, the famous Golden Age of Spanish Jewry (circa 900–1200) produced such luminaries as: statesman and diplomat Hasdai ibn Shaprut, vizier and army commander Shmuel ha-Nagid, poet-philosophers Solomon Ibn Gabirol and Judah Halevi, and at the apex of them all, Moses Ben Maimon, also known among the Spaniards as Maimonides. A physician, philosopher, and religious legal scholar (“halakhist”), Maimonides was born and, during his early years, educated in Spain. Although he and his family were forced to flee the Iberian Peninsula due to Muslim persecutions, he continued to refer to himself as “HaSepharadi” or “the Spaniard” even though he eventually lived most of his life in Egypt. Such was the prestige and the heritage of Sephardim.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("The Contents of the Book")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("Mark R. Cohen, in "), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02"
    }
  }, [_vm._v("chapter 2")]), _vm._v(", outlines the beginning and early history of the Sephardim. Noting their similarities to both the Jews living under the crescent and the Jews under the cross, he traces their real origins to the medieval Arab world. Cohen goes on to carefully examine the political and cultural features of Jewish existence under Islam to better comprehend the special character of Sephardic Jewry. He notes its many distinctive features: “its sense of noble descent; tradition of service to gentile rulers; experience of high cultural achievement in philosophy and poetry; "), _c('a', {
    attrs: {
      "id": "page_10"
    }
  }), _vm._v("feeling of superiority to Ashkenazic Jewry; and history of crypto-Judaism, the practice of Judaism in secret after conversion to Christianity under duress.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("While rejecting the “myth of the interfaith utopia” and “the myth of Sephardic supremacy” proposed by some nineteenth-century Jewish historiographers, he nonetheless notes that medieval Jewish life in the Arab world, especially in contrast to Jewish life under Christendom during that same time, was far more favorable and secure. Cohen’s essay provides the reader with a clear, concise comparison between Jewish life under the cross and Jewish life under the crescent, delineating the various advantages and disadvantages Jews experienced in both circumstances and attempting to explain why.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Cohen also addresses in his essay the persistence of the term “Sephardic” to encapsulate all non-Ashkenazi Jews, even those who clearly lived in lands where Sephardim never dwelt in any numbers. He asserts that in part, this misnomer reflects the overwhelming historical influence that Jews from Spain had on other Jewries, particularly those living within the Ottoman Empire (which at its height encompassed all of the territories around the eastern Mediterranean). Overall, Cohen provides great insight into the link between Sephardic civilization as a whole and its roots in the medieval Arab world.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The authors of "), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02"
    }
  }, [_vm._v("chapters 2")]), _vm._v(" through "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch05#ch05",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5")]), _vm._v(" of this book examine, in one way or another, a wide spectrum of Jewish creativity during the Muslim era and beyond. In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch03#ch03",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 3")]), _vm._v(", Norman A. Stillman surveys the development and utilization of Judeo-Arabic through early, medieval, and modern times. He claims that amongst all the many Diaspora languages of post-Talmudic times, including Yiddish and Ladino, Judeo-Arabic is the premier Diaspora language. Over the last 1400 years, it was spoken by more Jews than any other language and was used by Jews across a greater geographical expanse (in the Middle Ages, from Spain to India) than any other Jewish language. Moreover, after Hebrew and Aramaic, it enjoys the longest history (from the ninth century to the present).")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Stillman indicates that “Judeo-Arabic was the literary medium for some of the greatest works of the Jewish spirit” and sets out to enumerate the vast and varied literature written in it. He notes that medieval Judeo-Arabic literary culture reached its apex in Muslim Iberia, where Jewish scholars and men of letters produced works in Judeo-Arabic on Hebrew grammar, lexicography, prose, and philosophy. While Spain’s Judeo-Arabic "), _c('a', {
    attrs: {
      "id": "page_11"
    }
  }), _vm._v("tradition continued even after Sephardic Jews fled Muslim persecutions—most notably in Maimonides’ major works, with the exception of the halachic code "), _c('em', [_vm._v("Mishnah Torah")]), _vm._v("—its usage changed in the centuries following the Golden Age. Like Yiddish and Ladino, Stillman acknowledges that today Judeo-Arabic is a dying language. However, he maintains, “it lives on in a myriad of ways through the maintenance of traditional practices, the ever-increasing scholarly study of Judeo-Arabic language, literature, and history, particularly in Israel and France, but also in North America, and through the popular interpretations and translations of Judeo-Arabic wit and wisdom.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch04#ch04",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 4")]), _vm._v(", David M. Bunis examines how and when Judeo-Spanish culture was born in medieval Spain and grew to prominence as Judeo-Arabic lost its foothold during the Christian Reconquista. As Arabic cultural influence declined, Jewish contact with speakers of Castilian and other Romance languages led to a re-Hispanization and a return to Ibero-Romance. Bunis notes that Sephardic Jews embraced the traditions of Hispanic oral folk literature, including proverbs, ballads, popular songs, stories, and legends. After the expulsion from Spain, Sephardim then carried Jewish Ibero-Romance languages and cultures to the lands that offered them shelter: the Ottoman Empire, North Africa and the Middle East, and parts of Western Europe, especially Italy. In time, among the many varieties of dialects that had been spoken in Spain, only Castilian remained, becoming the predominant Jewish language of all Jews in much of the Mediterranean region, thus attesting to the influence and authority of the Spanish exiles in their new lands.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Bunis outlines how over the centuries the language received many names such as Ladino, Judezmo, and in Morocco, Hakitia. In addition, he traces the changes Judeo-Spanish went through as it evolved in lands beyond Spain. Through his assessment of the language, written literature, and numerous oral traditions of the Sephardim following their expulsion from Spain, Bunis demonstrates the rich contribution made in this language as well as the impact of other societies on Sephardic Jewish culture. In the modern period, the spread of education in western languages, the rise of local nationalism, pressure to conform linguistically and culturally, emigration from the Mediterranean postexpulsion home regions, and the devastating effects of World War II on the Sephardic communities of Europe, all contributed to a decline in Judeo-Spanish culture. Despite the negative impact of these factors, the culture continues to be maintained "), _c('a', {
    attrs: {
      "id": "page_12"
    }
  }), _vm._v("today as the modern heir to a noble, centuries-old, East-West Jewish heritage due to the efforts of writers, scholars, performers, and other activists in the surviving speech communities.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch05#ch05",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 5")]), _vm._v(", Jonathan P. Decter’s essay surveys the belletristic writing of medieval Sephardim, first focusing on their literary production in Andalusia under Muslim rule and then under Christian hegemony. He begins by tracing the development and revolutionary changes that took place in the sphere of Hebrew poetry in the tenth century. One may see that Hebrew retained premier status as the language of literature, which demonstrates the impulse to maintain community and fortify identity, even as Jewish writings in other languages testified to a high degree of Jewish acculturation within Islamic and Christian cultures. The author indicates that the choice of Hebrew as a poetic language can be viewed as one of the greatest expressions of Jewish self-assertion, even nationalist aspiration, during this period.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In time, Jews either moved from Islamic al-Andalus"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en21"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en21"
    }
  }, [_vm._v("21")])]), _vm._v(" to Christian Spain due to persecutions or fell under Christian rule as the Reconquista progressed. Decter seeks to show how the Hebrew literature of Christian Spain is understood to be both a continuation of the tradition of al-Andalus and an innovative corpus of its own. He provides examples to illustrate this contention from the field of Hebrew poetry and prose, particularly investigating the influence of the Arabic “maqama” (a rhymed prose fictional narrative derived from an eastern Arabic form in which rhymed metered poems were interspersed in a loose rhyming prose). Finally, he notes the beginnings of literary works in Judeo-Spanish, which include translations of previous works in Hebrew and Judeo-Arabic as well as ballads, proverbs, and love songs. Throughout his essay, Decter allows us glimpses into the lives of several poets and authors such as Solomon Ibn Gabirol and Judah al-Harizi, providing the reader with insight into the concerns and issues prevalent during this era. Overall, the author helps us to appreciate the multifaceted position Jews occupied within the complex cultural environment of medieval Iberia and to assess the degree of porousness between Jewish and non-Jewish culture.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The centrality of the Hebrew language was celebrated and honored by medieval Sephardim in another form of literature known as biblical exegesis. In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch06#ch06",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 6")]), _vm._v(", Isaac Kalimi examines how Jewish biblical commentary flourished among such Diaspora communities in Northern France, Provence, Spain, and parts of the Middle East. Within the Sephardic community as a whole, the two main groups that engaged in biblical interpretation "), _c('a', {
    attrs: {
      "id": "page_13"
    }
  }), _vm._v("were the Karaites"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en22"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en22"
    }
  }, [_vm._v("22")])]), _vm._v(" and the Rabbinites,"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en23"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en23"
    }
  }, [_vm._v("23")])]), _vm._v(" who often engaged in intense polemical disagreements regarding each other’s perspectives. In his essay, Kalimi focuses particularly on two extraordinary biblical commentators, philologists, and philosophers—Rav Saadia Gaon and Rabbi Abraham ibn Ezra. Though they lived in entirely different eras and countries, these prolific exegetes shared several common methodological approaches to biblical interpretation, and each disputed repeatedly with commentators from the Karaite community. By examining their attempts at defending the rabbinic tradition against the Karaite challenge, the reader begins to comprehend some of the religious, cultural, and political elements operative in their respective eras as well as the vital connection between the Jewish people and the “Book of Books.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Indeed, socially and politically, with the transition from the Islamic regime to the Christian one, the seeds of the destruction of the Jewish community in Spain had been planted. However, it is important to note that according to Gerber,")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("for a while, the Sephardim were able to sustain their new forms of creativity in the Christian North. Removed from the special atmosphere and human alchemy of al-Andalus, however, the unique symbiosis of Jewish and Muslim culture did not survive long, and a new era in the Sephardic life began to unfold in Christian Spain. There, the intellectual heirs of the Golden age, whether philosophers or translators in Southern-France, or poets, mathematicians and scientists in Castile and Aragon, were forced to contend with unfamiliar forces of reaction and repression. No longer would their heritage of synthesizing secular and religious learning be considered appropriate or acceptable."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en24"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en24"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("One of the more distinctive forms of Sephardic spiritual and intellectual creativity during the Christian era was Kabbalah, which embodied a mixture of continuity and innovation in the subject of Jewish mysticism.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The first section of the book concludes with Moshe Idel’s thought-provoking chapter on a topic that is so distinctive to Sephardic creativity, Jewish Philosophy and Kabbalah. In an attempt to gain a better understanding of the emergence and influence of philosophy and Kabbalah throughout the Sephardic world, Idel examines the surrounding non-Jewish cultures that hosted these two forms of religious and intellectual expression. In the case of Jewish philosophy, the Islamic world was instrumental in its materialization and development; whereas earlier phases of "), _c('a', {
    attrs: {
      "id": "page_14"
    }
  }), _vm._v("Kabbalah (the most important form of Jewish medieval mysticism), evolved under Christian rule."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en25"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en25"
    }
  }, [_vm._v("25")])]), _vm._v(" Furthermore, the phenomenon of continuity and change regarding intellectual Sephardic life is also evidenced in Idel’s essay.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Toward the end of the fourteenth century, a radical change for the worse took place in the political and religious life of the Jews living on the Iberian Peninsula. In 1391, severe persecutions broke out, this time provoked by the preaching of Dominican monks. This led many Jews to convert to Christianity in order to save their lives. Many of these “New Christians,” or “conversos” (who were known derogatively as “Marranos,” meaning “swine,” but called themselves “anusim,” meaning “ones who were forced”) accepted Christianity but practiced Judaism underground and taught their children to do the same. The Inquisition was consequently established by the Church to uncover these secret (or “crypto-”) Jews and prevent them from relapsing back to Judaism. However, the existence of outwardly practicing Jews, often supportive of the conversos, many of whom were family members, was a constant thorn in the side of the Church."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en26"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en26"
    }
  }, [_vm._v("26")])]), _vm._v(" Finally, under strong pressure from the Dominicans, Queen Isabella and King Ferdinand issued an edict in March 1492, permanently expelling from Spain all Jews who refused to accept Christianity. This edict was not officially repealed until 1968.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Thousands of Jews accepted conversion; thousands more left the country. Those who emigrated eastward primarily settled in areas already or soon to be contained in the Ottoman Empire,"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en27"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en27"
    }
  }, [_vm._v("27")])]), _vm._v(" where the Jews were openly welcomed. The rest settled in Morocco to the south or in Holland (Amsterdam) to the north. With the expulsion from Spain, a new era in the history of the Sephardim began.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("From Expulsion to the Modern Era: Exile, Decline, and Revival")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("The Edict of Expulsion in 1492 by no means brought an end to Spanish or Sephardic Jewry. The actions leading to the actual expulsion, however, brought to a close an important era in Jewish history. In medieval Spain, a rich secular and sacred creativity influenced by the unique interactions of Jews, Christians, and Muslims had flourished. This tradition, though it had developed there, continued to live throughout the centuries, in one form or another, all the way to the present day.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_15"
    }
  }), _vm._v("In very general terms, the Sephardic emigration from Spain proceeded in two general geographical directions, either north and west to Europe and the Americas or east and south to the Mediterranean basin and North Africa. The sagas, traditions, and languages, as well as the modes of religious and cultural expression of the exiles and their descendants represent great diversity, reflecting both the Spanish heritage they carried with them and the cultures of the lands in which they established residence. In northwestern Europe—Holland, France, England, and, ultimately by extension, the Americas—the Sephardic Jews, in time, assimilated to the prevailing Jewish (and later secular) cultural milieu far more than those who migrated east and south did. Due to their large numbers in various communities of the Ottoman Empire and North Africa, the Sephardim established major communities where Iberian Jewish culture not only was preserved, but also thrived. Despite the economic and intellectual decline that took place in the eighteenth and part of the nineteenth centuries, Ottoman Sephardic culture survived until the eve of the Holocaust."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en28"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en28"
    }
  }, [_vm._v("28")])]), _vm._v(" Largely due to twentieth-century settlement in North America and Israel, Sephardic and Mizrahi Jewish identity and culture are enjoying a revival.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In the second part of the book, Annette B. Fromm examines one of the most creative communities in the Sephardic Diaspora. In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch08#ch08",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 8")]), _vm._v(", Fromm traces the experiences of the Jews from the Iberian Peninsula to the Ottoman Balkans as they encounter their new multicultural context. She lends insight into how the Sephardim preserved and augmented their culture and traditions during the four centuries following exile. Her analysis focuses on the cultural diversity in which they flourished while residing in Spain, the character of the communities that preceded them in the Ottoman Empire, and how the Hispanic culture they transported came to influence the native populations they encountered in their new lands.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("While Fromm’s essay in large part covered the sociohistorical aspects of the Sephardic Diaspora in the Ottoman Empire, the next two essays, by Zvi Zohar and Morris M. Faierstein, delve into the religious and intellectual history of the Sephardic Diaspora. Zvi Zohar, in "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch09#ch09",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 9")]), _vm._v(", explores the development of Sephardic halakha, from the Spanish expulsion to the present. It traces the renewal of Sephardic scholarship in the exilic communities of the sixteenth century as well as the creative currents of Sephardic halakha during the seventeenth and eighteenth centuries. While limitations on communal autonomy, emancipation, increasing Westernization, and other changes radically transformed the conditions of "), _c('a', {
    attrs: {
      "id": "page_16"
    }
  }), _vm._v("Sephardic Jewish existence and led to some dwindling of first-rate talent in the rabbinate, Sephardic halakhic activity continued vigorously during the nineteenth and twentieth centuries, without falling into the schisms that characterized European Jewry. The two major figures of Sephardic juris-prudence in the second half of the twentieth century were Hayyim David HaLevi (1924–1998) and Ovadia Joseph (b. 1924). Ovadia’s vision of “return” to an earlier “state of grace” within halakha differed from the ethos and vision articulated by HaLevi and earlier great figures in the Sephardic classic halakhic tradition. HaLevi and others championed the tradition of halakha as a forward-looking, innovative religious phenomenon transforming itself organically through the dynamic response of halakhic masters to changes in social history and culture. Intriguingly, the very same rabbis who were great legalists, making bold attempts to establish halakhic conditions they hoped were appropriate to Messianic times, also engaged in mystical speculation and were involved in the well-known Kabbalistic revolution that took place in sixteenth-century Safed.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch10#ch10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 10")]), _vm._v(", Morris Faierstein examines the impact of the sixteenth-century spiritual revolution in Safed on the beliefs and traditions of Judaism as it is practiced throughout the world. He asserts that the exiled Jews of Spain and their descendants played a central role in the development of the esoteric and exoteric teachings and rituals that first began as the practices of mystical brotherhoods in Safed in 1492. Kabbalistic opinions concerning the development of Sephardic Judaism during this time ranged from a quietist approach to a more apocalyptic reaction. While some Jews hoped for a hurried recovery, others sought signs of the forthcoming redemption. Many saw the expulsion from Spain as perhaps "), _c('em', [_vm._v("the")]), _vm._v(" sign of tribulation that would herald the beginning of the Messianic Age as foretold in Rabbinic literature.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("As with the first section of this book, "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/pt02#pt02",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("part II")]), _vm._v(" also concludes fittingly with the lesser-known history of the vital role that women have played in the Ottoman Empire, the Sephardic Diaspora’s most populous region. In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch11#ch11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 11")]), _vm._v(", Paméla Dorn Sezgin studies the lives of Sephardic women as compared to those of men, looking at historiography, Judeo-Spanish culture, and social organization and decline. First, the author focuses on the general, sociocultural, and historical milieu in which women of different social classes lived. She compares the lives of women from minority groups with those of their Muslim counterparts.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Second, she identifies elements that are unique to Jewish women, such as their adaptation to the historical events and culture particular to Sephardic "), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }), _vm._v("Jewry. Her commentary serves as a bridge to help the reader make the transition from one era to the next. Sezgin demonstrates how traditional Judeo-Spanish culture, which was preserved by almost 450 years under Ottoman rule, has given way in modern times under the onslaught of increasing societal changes, particularly those that have altered longstanding patterns in women’s lives. She notes how practices such as mandatory schooling for women, women’s emancipation at the ballot box, increasing options to work outside of the home and become self-supporting, and greater fluency in national languages, have all enabled greater participation in the majority culture and served to transform the everyday life of Sephardic women.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('em', [_vm._v("Sephardic Jewry in the Modern Era and Special Topics")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("From the seventeenth century onward, the prior prominence of Sephardic Jewry within world Jewry began to diminish as Ashkenazi Jewry grew in significance and size. The Sephardim continued their predominance among many of the communities of North Africa, the Middle East, Italy, and Asia Minor."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en29"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en29"
    }
  }, [_vm._v("29")])]), _vm._v(" However, throughout the Jewish Diaspora, Ashkenazim quickly outnumbered Sephardim and Oriental Jews.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Toward the end of the nineteenth century, persecutions and extreme poverty within Russia’s pale of settlement caused a massive Ashkenazi emigration from eastern to western Europe and on toward South Africa, Australia, and the United States. Ashkenazi Jewry rapidly swelled to 90 percent or more of the population in these diverse but increasingly prominent Jewish communities. However, the population decline of Sephardic and Oriental Jews is by no means the last word on their relevance in the modern era. In Israel, Sephardic and Oriental Jews constitute approximately half of the Jewish population. Their role in shaping Israeli society and politics continues to be a vital one, and by extension, they have affected the identity of world Jewry as a whole.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch12#ch12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Chapters 12")]), _vm._v(" through "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch14#ch14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("14")]), _vm._v(" engage in a discourse regarding three aspects of Sephardic and Oriental Jewry in the modern era outside of the Land of Israel—Diaspora identities and attitudes toward the “Other,” the development of Sephardic and Oriental modes of music, and the impact of the Holocaust.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In transition to the modern era, "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/pt03#pt03",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("part III")]), _vm._v(" of this book opens with the essay by Jonathan Schorsch, "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch12#ch12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 12")]), _vm._v(". The perceived importance of being "), _c('a', {
    attrs: {
      "id": "page_18"
    }
  }), _vm._v("Iberian is reflected in Sephardic discourse regarding race and blackness in the Atlantic world from the fifteenth to the eighteenth centuries. Schorsch asserts that this self-perception as Iberians helped Spanish and Portuguese Jews as well as (ex-)conversos navigate their ambiguous status as both religious outsiders and racial and cultural insiders (as whites).")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Schorsch explores the relations and mutual imagining of Sephardic Jews and black Africans under the pressures of Iberian anti-Jewish hysteria and the incipient racism generated by European overseas expansion. The behavior of Sephardim in the Atlantic world toward blacks in general and their own slaves in particular so closely resembled that of their host populations, and often is so lacking in Jewish particularities, that one can forget at times that this slave-owning minority was a severely ostracized and persecuted one.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Schorsch attempts to describe this facet of the Sephardic cultural history by turning to a wide variety of sources, many hitherto ignored, such as Sephardic biblical exegesis, halakhic writings, quasiscientific literature, sermons, poetry, letters, notarial records, and archival sources. The essay undoubtedly adds another layer of distinctiveness to this volume.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch13#ch13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 13")]), _vm._v(", Mark Kligman studies the variability and distinctiveness of Sephardic liturgical music beginning with the Ottoman Empire. Sephardic liturgical music is a true reflection of the cultural diversity of Sephardic Jews, reflecting a wide range of musical styles—cantillation, liturgical song, and “piyyutim”—that have absorbed, adapted, and reacted to a variety of influences over the ages. By focusing on the liturgical traditions of European and Middle Eastern Jewish communities, Kligman demonstrates the similarities and differences among the various Sephardic practices. He notes how the Spanish and Portuguese tradition is Western in its approach to liturgical music, using melodies that are stylistically Western. In contrast, the Middle Eastern Jewish tradition, known as “Yerushalmi Sephardim,” displays a considerable amount of Arab influence. Other communities display a more blended style. The Moroccan or North African Tradition contains some Western musical elements but is mixed with the Andalusian Spanish style, producing ornate melodic patterns. The Turkish or Ottoman Jewish tradition reflects a mixed musical tradition that contains elements of Arab, Spanish, and Balkan styles.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("According to Kligman, the uniqueness of Sephardic and Oriental Jewish music is exemplified by the manner in which it is integrated into Jewish life. As traditions evolve, so does Sephardic music, inspiring both religious and secular musical composition. Sephardic music’s connection "), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _vm._v("to Jewish culture has persisted despite the greater Ashkenazi religious influences and institutions both in the Americas and in Israel today.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In focusing on modern Jewish life, the Shoah, the decimation of European Judaism, is clearly a seminal event that must be addressed. What is largely overlooked and unknown is its enormous effect upon Sephardic Jewry. Henry Abramson addresses this lacuna in "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch14#ch14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 14")]), _vm._v(". By contrasting Sephardic experiences in Greece, Yugoslavia, Serbia, Croatia, Algeria, Tunisia, Morocco, and Libya before and during the Holocaust, Abramson gives insight into the technical as well as intellectual factors that create this double occlusion of being ignored by the public and academia alike.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("While world Jewry was involved in assessing the damage of the Holocaust and contemplating ways of recovery, the establishment of the future Jewish state was under way. In the closing chapter of this volume, I explore the life of the Sephardic and Oriental Jewry in Israel. The majority of Ashkenazi Jews who moved to Palestine in the beginning of the twentieth century were Western, secular, and socialist in orientation. These immigrants became both the political leaders and cultural architects of the emerging State of Israel. When Sephardi Jews immigrated to Israel mainly between 1948 and the mid-1960s, they found a very different culture and society from the one to which they had grown accustomed in their countries of origin. Under Ashkenazi leadership, to be a “good Israeli” meant subscribing to socialist ideals, living out Western values, and rejecting all but the most modern adaptations of religious identity."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch01.en30"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01en30"
    }
  }, [_vm._v("30")])]), _vm._v(" Naturally, this paradigm presented a serious problem for Sephardi immigrants and their children. To accept it, meant to reject their past, their traditions, and their very sense of self. Yet, to reject the Ashkenazi-defined ideal meant to reject becoming fully “Israeli.” Caught in this paradox, many Sephardic youth were not wholly able to integrate their Sephardic heritage with their sense of Israeli identity, consequently suffering a loss of pride and self-esteem. Even within the Orthodox world, Ashkenazi Jews dominated the yeshivoth and academies of learning, compelling Sephardim to model themselves after an Ashkenazi-defined religious model.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Today, Sephardic culture and leadership have reached a critical point. After years of modeling themselves after Ashkenazi Jews, Sephardi and Oriental Jews have come perilously close to losing their sense of self and their rich cultural heritage. In spite of the pressures arrayed against them in recent years, Sephardic and Oriental Jews, among the observant and even more so among the secular population, are rebelling, as they seek to return to their roots and from them create an old-new Sephardi paradigm.")]), _vm._v(" "), _c('h4', {
    staticClass: "h4"
  }, [_c('a', {
    attrs: {
      "id": "page_20"
    }
  }), _c('span', {
    staticClass: "small"
  }, [_vm._v("NOTES")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("I wish to thank two very helpful colleagues, Professors Henry Abramson of Florida Atlantic University and Norman Stillman from the University of Oklahoma, who kindly gave of their valuable time to look over the preliminary draft of this introductory essay and offered their excellent suggestions.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en1"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en1"
    }
  }, [_vm._v("1")]), _vm._v(". "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BCE")]), _vm._v(" (Before the Common Era) and "), _c('span', {
    staticClass: "small"
  }, [_vm._v("CE")]), _vm._v(" (Common Era) are the scholarly alternatives to "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BC")]), _vm._v(" and "), _c('span', {
    staticClass: "small"
  }, [_vm._v("AD")]), _vm._v(", which have religious connotations.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en2"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en2"
    }
  }, [_vm._v("2")]), _vm._v(". Jeremiah 40–44.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en3"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en3"
    }
  }, [_vm._v("3")]), _vm._v(". In Greece, an inscription from the third century "), _c('span', {
    staticClass: "small"
  }, [_vm._v("BCE")]), _vm._v(" refers to a Jewish slave. See Haim Hillel Ben-Sasson, "), _c('em', [_vm._v("The History of the Jewish People")]), _vm._v(" (Cambridge: Harvard University Press, 1976), 278.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en4"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en4"
    }
  }, [_vm._v("4")]), _vm._v(". Ibid., 277–78.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en5"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en5"
    }
  }, [_vm._v("5")]), _vm._v(". Ibid., 307.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en6"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en6"
    }
  }, [_vm._v("6")]), _vm._v(". Ibid., 277.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en7"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en7"
    }
  }, [_vm._v("7")]), _vm._v(". The Jews of India and China lived under Hinduism, Buddhism, and other religions of the area. However, these Jews’ origins were often traced to Oriental Jewish communities further west, such as Yemen, Syria, and Iraq, where they lived under Islam.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en8"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en8"
    }
  }, [_vm._v("8")]), _vm._v(". For a graphic representation of the periods of settlement and migration, see Martin Gilbert, "), _c('em', [_vm._v("The Atlas of Jewish History")]), _vm._v(" (New York: William Morrow, 1992), 17, which shows a Jewish presence already by "), _c('span', {
    staticClass: "small"
  }, [_vm._v("100 CE")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en9"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en9"
    }
  }, [_vm._v("9")]), _vm._v(". See ibid., 47, for a map of expulsions from 1000 to 1500. Also Ben-Sasson, "), _c('em', [_vm._v("History")]), _vm._v(", 465, 486–87.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en10"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en10"
    }
  }, [_vm._v("10")]), _vm._v(". Bernard Lewis, "), _c('em', [_vm._v("The Jews of Islam")]), _vm._v(" (Princeton, NJ: Princeton University Press, 1984).")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en11"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en11"
    }
  }, [_vm._v("11")]), _vm._v(". There had been several instances, most notably in 1391 in many cities in Castile, where Jews were given the choice to convert to Christianity or die, and so fled before 1492.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en12"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en12"
    }
  }, [_vm._v("12")]), _vm._v(". The Ottoman Empire then encompassed the Balkans, Greece, Asia Minor, Lebanon, Syria, Palestine, and Egypt.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en13"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en13"
    }
  }, [_vm._v("13")]), _vm._v(". Mark Cohen will address these distinctions in much more detail in "), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch02#ch02"
    }
  }, [_vm._v("Chapter 2")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en14"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en14"
    }
  }, [_vm._v("14")]), _vm._v(". Yitzhak Baer, "), _c('em', [_vm._v("A History of the Jews in Christian Spain")]), _vm._v(", vol. 1 (Philadelphia: Jewish Publication Society, 1961), 319, in which the author takes the statement from "), _c('em', [_vm._v("She’eloth u’Teshuboth ha-ROSH")]), _vm._v(", kelal 55, no. 9.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en15"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en15"
    }
  }, [_vm._v("15")]), _vm._v(". Hasdai ibn Shaprut, in his letter to Joseph, the king of the Khazar kingdom, describes himself as “belonging to the exiled Jews of Jerusalem, in Spain.” See Franz Kobler, "), _c('em', [_vm._v("Letters of Jews through the Ages")]), _vm._v(", vol. 1, "), _c('em', [_vm._v("From Biblical Times to the Renaissance")]), _vm._v(", East and West Library (New York: Hebrew Publishing, 1978), 98. See also "), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01"
    }
  }, [_vm._v("chapter 1")]), _vm._v(" in this volume.")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_c('a', {
    attrs: {
      "id": "page_21"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en16"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en16"
    }
  }, [_vm._v("16")]), _vm._v(". See Esther Benbassa and Aron Rodrigue, "), _c('em', [_vm._v("Sephardi Jewry: A History of the Judeo-Spanish Community, Fourteenth through Twentieth Centuries")]), _vm._v(" (Berkeley and Los Angeles: University of California Press, 2000), xxvi.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en17"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en17"
    }
  }, [_vm._v("17")]), _vm._v(". Eliyahu Ashtor, "), _c('em', [_vm._v("The Jews of Muslim Spain")]), _vm._v(" (Philadelphia: Jewish Publication Society, 1992), vol. 1, 11.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en18"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en18"
    }
  }, [_vm._v("18")]), _vm._v(". Ibid., 11.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en19"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en19"
    }
  }, [_vm._v("19")]), _vm._v(". Ibid., 11–16.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en20"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en20"
    }
  }, [_vm._v("20")]), _vm._v(". Though Ashkenazi Jewry would continue to follow its own rabbinic leadership, Sephardic and Oriental Jewry, who were the vast majority of world Jewry at this time, largely looked to Spanish-born rabbis for spiritual guidance.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en21"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en21"
    }
  }, [_vm._v("21")]), _vm._v(". Al-Andalus is known in English as Andalusia.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en22"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en22"
    }
  }, [_vm._v("22")]), _vm._v(". Karaites (literally “scripturalists”) are an offshoot of Judaism thought to have been founded in eighth-century Babylonia. They flourished between the late ninth century and the eleventh century and are distinguished from Rabbanites by their rejection of the Talmud and rabbinical tradition. Their liturgy, calendar, and many rituals differ markedly from rabbinic Judaism. For more about Karaites, see Nathan Schur, "), _c('em', [_vm._v("History of the Karaites")]), _vm._v(" (Frankfurt: Peter Lang, 1992); Leon Nemoy, "), _c('em', [_vm._v("Karaite Anthology")]), _vm._v(" (New Haven: Yale University Press, 1987); and Daniel J. Lasker, “Rabbinism and Karaism: The Contest for Supremacy,” in "), _c('em', [_vm._v("Great Schisms in Jewish History")]), _vm._v(", ed. Raphael Jospe and Stanley M. Wagner (New York: Ktav, 1994).")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en23"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en23"
    }
  }, [_vm._v("23")]), _vm._v(". Rabbinites are followers of the rabbinical tradition detailed in the Talmud and other works of oral law. The name was coined by the Karaites to characterize their opponents.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en24"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en24"
    }
  }, [_vm._v("24")]), _vm._v(". Jane Gerber, "), _c('em', [_vm._v("The Jews of Spain: A History of the Sephardic Experience")]), _vm._v(" (New York: Free Press, 1992), 89.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en25"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en25"
    }
  }, [_vm._v("25")]), _vm._v(". In "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch07#ch07",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 7")]), _vm._v(", Idel also asserts that although the Jewish exile from Spain was traumatic, ironically, it provided the refugees with a sense of liberty, by releasing them from the pressures of Spanish authorities and the Inquisition. Though a comprehensive survey of the postexpulsion Kabbalah will be treated in "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch10#ch10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 10")]), _vm._v(" of this book, it is important to note that according to Idel, the creativity exhibited by sixteenth-century Sephardi Kabbalists may be related more to a sense of urgency and responsibility toward rebuilding the Sephardic communities in new places, than to lamenting the loss of “Sepharad” and the tribulations created by the expulsion. In contrast, Gershom Scholem saw the emergence of Lurianic Kabbalah in sixteenth-century Safed primarily as a response to the expulsion of the Jewish community from Spain in 1492. In asserting this viewpoint, Scholem was following a trend that goes back to nineteenth-century German Jewish historiography, in which mystical movements were viewed as responses to historical events. Moshe Idel and others have recently challenged this explanation of the relationship between the Spanish expulsion and the rise of Lurianic Kabbalah. See Gershom Scholem, "), _c('em', [_vm._v("Major Trends in Jewish Mysticism")]), _vm._v(" (New York: Schocken, 1961), "), _c('a', {
    attrs: {
      "id": "page_22"
    }
  }), _vm._v("244–51; idem, "), _c('em', [_vm._v("The Messianic Idea in Judaism")]), _vm._v(" (New York: Schocken, 1995), 41; idem, "), _c('em', [_vm._v("Kabbalah")]), _vm._v(" (New York: Dorset, 1987), 68. For a different perspective, see Moshe Idel, "), _c('em', [_vm._v("Messianic Mystics")]), _vm._v(" (New Haven: Yale University Press, 1998), 152–53.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en26"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en26"
    }
  }, [_vm._v("26")]), _vm._v(". Moreover, the Inquisition was authorized to prosecute only self-professed Catholics, not Jews, who still had a legal right to practice their religion.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en27"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en27"
    }
  }, [_vm._v("27")]), _vm._v(". These areas were Greece, Bulgaria, Yugoslavia, Turkey, the Levant, Egypt, Libya, Tunisia, and Algeria.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en28"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en28"
    }
  }, [_vm._v("28")]), _vm._v(". A detailed discussion of the effects of the Holocaust on Sephardic Jews can be found in "), _c('span', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch14#ch14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 14")]), _vm._v(" in this book.")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en29"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en29"
    }
  }, [_vm._v("29")]), _vm._v(". Howard M. Sachar, "), _c('em', [_vm._v("Farewell España: The World of the Sephardim Remembered")]), _vm._v(" (New York: Random House, 1998), 193–95 (on their influence in Asia Minor and the Balkans), 76–90 (in Morocco), 211–13 (in Italy), 257–58 (in Bulgaria).")]), _vm._v(" "), _c('p', {
    staticClass: "endnote"
  }, [_c('a', {
    attrs: {
      "id": "ch01en30"
    }
  }), _c('a', {
    staticClass: "nounder",
    attrs: {
      "href": "#/xhtml/ch01#ch01.en30"
    }
  }, [_vm._v("30")]), _vm._v(". See Amnon Rubenstein, “The End of the Sabra Myth,” in "), _c('em', [_vm._v("The Zionist Dream Revisited: From Herzl to Gush Emunim and Back")]), _vm._v(" (New York: Schocken, 1984), 138–39. He specifically addresses the Sabra image, stating: “The typical Sabra has always been depicted as an Ashkenazi son of European parents…. To the new arrivals from North Africa and other Moslem countries, the Sabra image was so remote that even attempts at assimilation were inconceivable.”")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-5f7044d0","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/ch01.vue
// module id = 81
// module chunks = 0